import { cn } from '@/lib/utils'
import type { DimensionKey, MatchDimensions } from '@/types'

/**
 * Five-row breakdown of a match's per-dimension scores (1.0-5.0 each)
 * with rationale text below each bar. Renders only when at least one
 * dimension is populated; legacy match rows render nothing.
 */

interface DimensionBarsProps {
  match: MatchDimensions
  className?: string
}

interface Row {
  key: DimensionKey
  label: string
  description: string
}

// Order chosen to mirror the server-side weight ordering: highest-weight
// dimension first so the most consequential signal is read first.
const ROWS: Row[] = [
  { key: 'cv_match', label: 'Skills + experience', description: 'How well your background fits' },
  { key: 'north_star', label: 'Career trajectory', description: 'Does this advance your goals' },
  { key: 'comp', label: 'Compensation', description: 'Salary signal vs market' },
  { key: 'cultural', label: 'Culture & growth', description: 'Company signals' },
  { key: 'red_flag', label: 'Posting quality', description: '5 = clean, 1 = many red flags' },
]

function scoreColor(score: number): { bar: string; text: string } {
  // Same banding logic as letter grades — keeps the visual story consistent.
  if (score >= 4.5) return { bar: 'bg-emerald-500', text: 'text-emerald-700' }
  if (score >= 4.0) return { bar: 'bg-lime-500', text: 'text-lime-700' }
  if (score >= 3.5) return { bar: 'bg-yellow-500', text: 'text-yellow-700' }
  if (score >= 3.0) return { bar: 'bg-orange-500', text: 'text-orange-700' }
  return { bar: 'bg-red-500', text: 'text-red-700' }
}

export function DimensionBars({ match, className }: DimensionBarsProps) {
  const dimScore = (key: DimensionKey): number | null => {
    const raw = match[`${key}_score` as keyof MatchDimensions]
    if (raw == null) return null
    return typeof raw === 'string' ? Number.parseFloat(raw) : (raw as number)
  }

  const hasAny = ROWS.some((r) => dimScore(r.key) != null)
  if (!hasAny) return null

  const rationale = match.dimension_rationale ?? {}

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {ROWS.map((row) => {
        const score = dimScore(row.key)
        if (score == null) return null
        const pct = (score / 5) * 100
        const { bar, text } = scoreColor(score)
        return (
          <div key={row.key} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-gray-900">{row.label}</span>
              <span className={cn('text-sm font-semibold tabular-nums', text)}>
                {score.toFixed(1)}
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              <div
                className={cn('h-full rounded-full transition-all', bar)}
                style={{ width: `${pct}%` }}
                aria-label={`${row.label} score ${score.toFixed(1)} of 5`}
              />
            </div>
            {rationale[row.key] ? (
              <p className="text-xs text-gray-600 leading-snug">{rationale[row.key]}</p>
            ) : (
              <p className="text-xs text-gray-400 italic leading-snug">{row.description}</p>
            )}
          </div>
        )
      })}
    </div>
  )
}
